import { useCallback, useRef } from 'react';

import {
   CurrentPractitionerEwusLoginCommand,
   EwusAccountBlockedFault,
   EwusAccountLoginFault,
   EwusPasswordExpiredFault,
} from '@dreryk/egabinet-ewus-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { PromiseVoidFunction } from '@dreryk/react-common';

import { NfzMfaTotpErrorSetter, nfzMfaTranslationKeys } from '~libs.desktop.shared.account';

type UseLogInToEwusOptions = {
   onSuccess: PromiseVoidFunction;
};

type LogInToEwus = (mfaTotp: string, setError: NfzMfaTotpErrorSetter) => Promise<void>;

export const useLogInToEwus = (options: UseLogInToEwusOptions): LogInToEwus => {
   const setMfaTotpErrorRef = useRef<NfzMfaTotpErrorSetter | null>(null);

   const reportFault = useCallback((message) => {
      setMfaTotpErrorRef.current?.(message);
   }, []);

   const { executeAsync: logIn } = useCqrsCommand<
      void,
      { mfaTotp: string },
      EwusAccountLoginFault | EwusPasswordExpiredFault | EwusAccountBlockedFault
   >(
      (parameters) => {
         const command = new CurrentPractitionerEwusLoginCommand();
         command.mfaTotp = parameters.mfaTotp;
         return command;
      },
      {
         onSuccess: options.onSuccess,
         onFault: {
            'egabinet-ewus-frontend-api:EwusAccountLoginFault': () => {
               reportFault(nfzMfaTranslationKeys.invalidCodeError);
            },
            'egabinet-ewus-frontend-api:EwusPasswordExpiredFault': () => {
               reportFault(nfzMfaTranslationKeys.passwordExpiredError);
            },
            'egabinet-ewus-frontend-api:EwusAccountBlockedFault': () => {
               reportFault(nfzMfaTranslationKeys.accountBlockedError);
            },
         },
      }
   );

   return useCallback(
      async (mfaTotp, setError) => {
         setMfaTotpErrorRef.current = setError;

         try {
            await logIn({ mfaTotp: mfaTotp });
         } catch {
            setMfaTotpErrorRef.current = null;
         }
      },
      [logIn]
   );
};
